import moment from "moment-timezone";
import { z } from "zod";

import { OperationResult, SpreadsheetRow } from "utils/spreadsheet/types";

import {
    CONTROL_TAGS_PREFIX,
    TASK_PREFIX,
    RECURRENCE_TYPES,
    VALID_DAYS,
    VALID_MONTHS,
    VALID_TWO_MONTHS,
    VALID_QUARTERS,
    VALID_HALF_YEARS,
    METADATA_IDENTIFIER_COLUMN,
    FIRST_DATA_ROW,
    TASK_FIELDS,
    FIRST_REVIEWER_TASK_PREFIX,
    SECOND_REVIEWER_TASK_PREFIX,
    COVERED_PERIOD_KEYS,
} from "./constants";
import { TagFieldsSchema } from "./tagParser";
import {
    TaskFieldsSchema,
    FirstReviewerTaskFieldsSchema,
    SecondReviewerTaskFieldsSchema,
} from "./taskParser";
import { RawControlRow, TagColumn, Task } from "./types";

/**
 * Schema validation module for bulk control import.
 * Parses and validates spreadsheet data using Zod schemas.
 */

/**
 * Parses and validates spreadsheet data for bulk control import
 *
 * @param rows - Raw spreadsheet rows to parse
 * @returns OperationResult containing validated rows or validation errors
 */
export function parseSchema(
    rows: SpreadsheetRow[]
): OperationResult<RawControlRow[]> {
    const cleanedRows = rows.filter(isValidSpreadsheetRow);

    const errors: string[] = [];
    const validRows: RawControlRow[] = [];

    cleanedRows.forEach((row, index) => {
        const unknownColumns = validateNoUnknownColumns(row);
        if (unknownColumns.length > 0) {
            errors.push(
                `Row ${
                    index + FIRST_DATA_ROW
                }: Invalid columns found: ${unknownColumns.join(", ")}`
            );
        }

        const validationResults = {
            control: RawControlBaseSchema.safeParse(row),
            tasks: TaskFieldsSchema.safeParse(row),
            firstReviewerTasks: FirstReviewerTaskFieldsSchema.safeParse(row),
            secondReviewerTasks: SecondReviewerTaskFieldsSchema.safeParse(row),
            tags: TagFieldsSchema.safeParse(row),
        };

        if (
            Object.values(validationResults).every((result) => result.success)
        ) {
            const controlData = (
                validationResults.control as z.ZodSafeParseSuccess<RawControlBaseRow>
            ).data;
            const tasksData = (
                validationResults.tasks as z.ZodSafeParseSuccess<{
                    tasks: Task[];
                    errors: string[];
                }>
            ).data;
            const firstReviewerTasksData = (
                validationResults.firstReviewerTasks as z.ZodSafeParseSuccess<{
                    tasks: Task[];
                    errors: string[];
                }>
            ).data;
            const secondReviewerTasksData = (
                validationResults.secondReviewerTasks as z.ZodSafeParseSuccess<{
                    tasks: Task[];
                    errors: string[];
                }>
            ).data;
            const tagsData = (
                validationResults.tags as z.ZodSafeParseSuccess<TagColumn[]>
            ).data;

            validRows.push({
                ...controlData,
                tasks: tasksData.tasks,
                firstReviewerTasks: firstReviewerTasksData.tasks,
                secondReviewerTasks: secondReviewerTasksData.tasks,
                tags: tagsData,
            });
        } else {
            const allErrors = Object.values(validationResults)
                .filter(
                    (
                        result
                    ): result is z.ZodSafeParseError<RawControlBaseRow> =>
                        !result.success
                )
                .flatMap((result) => result.error.issues);

            allErrors.forEach((error) => {
                const fieldPath = error.path.join(".");
                errors.push(
                    `Row ${index + FIRST_DATA_ROW}: ${fieldPath} - ${
                        error.message
                    }`
                );
            });
        }
    });

    if (errors.length > 0) {
        return { success: false, errors };
    }

    return { success: true, data: validRows };
}

// ----------------
// Type Definitions
// ----------------

// Represents a parsed control row before validation
export type RawControlBaseRow = z.infer<typeof RawControlBaseSchema>;

// ----------------
// Helper Functions
// ----------------

/**
 * Checks if a spreadsheet row contains actual data and is not a metadata row
 */
function isValidSpreadsheetRow(row: SpreadsheetRow): boolean {
    const hasData = Object.values(row).some(
        (value) => value !== undefined && value !== "" && value !== null
    );

    const isMetadataRow =
        typeof row[METADATA_IDENTIFIER_COLUMN] === "string" &&
        row[METADATA_IDENTIFIER_COLUMN].includes(" - ") &&
        row.Title === "" &&
        row.RecurrenceType === "";

    return hasData && !isMetadataRow;
}

/**
 * Validates that comma-separated values in a string are within a set of valid values
 */
function validateCommaSeparatedValues(
    value: string | undefined,
    validValues: readonly string[]
): boolean {
    if (!value) {
        return true;
    }
    const items = value.split(",").map((item) => item.trim());
    return items.every((item) => validValues.includes(item));
}

/**
 * Ensures that the spreadsheet row contains only known columns
 */
function validateNoUnknownColumns(row: SpreadsheetRow): string[] {
    const knownBaseFields = Object.keys(RawControlBaseSchema.shape);

    const taskPrefixes = [
        TASK_PREFIX,
        FIRST_REVIEWER_TASK_PREFIX,
        SECOND_REVIEWER_TASK_PREFIX,
    ];

    const unknownColumns = Object.keys(row).filter((key) => {
        if (knownBaseFields.includes(key)) {
            return false;
        }

        for (const prefix of taskPrefixes) {
            if (key.startsWith(prefix)) {
                const match = new RegExp(
                    `^${prefix}(\\d+)(${TASK_FIELDS.join("|")})$`
                ).exec(key);
                return !match;
            }
        }

        if (key.startsWith(CONTROL_TAGS_PREFIX)) {
            return (
                key === CONTROL_TAGS_PREFIX ||
                key.replace(CONTROL_TAGS_PREFIX, "").trim().length === 0
            );
        }

        return true;
    });
    return unknownColumns;
}

// ----------------
// Schemas
// ----------------

// Base control schema for validating static control fields
export const RawControlBaseSchema = z.object({
    ControlProgramName: z.string().min(1, "Control Program Name is required"),
    Title: z.string().min(1, "Title is required"),
    Description: z.string().optional(),
    CoveredPeriod: z
        .string()
        .optional()
        .transform((val) => (!val || val.trim() === "" ? "None" : val))
        // TODO: Not ideal, but CurrentTwoMonthPeriod is more user friendly than CurrentPeriod
        .transform((val) =>
            val.trim() === "CurrentTwoMonthPeriod" ? "CurrentPeriod" : val
        )
        .pipe(z.enum(COVERED_PERIOD_KEYS)),
    LoginRequired: z
        .string()
        .default("")
        .refine(
            (val) => !val || ["Y", "N"].includes(val.toUpperCase()),
            "Must be Y or N"
        )
        .transform((val) => val.toUpperCase() === "Y"),
    Calendar: z.string().optional(),
    RecurrenceType: z.enum(RECURRENCE_TYPES),
    WeeklyRecurrenceDays: z
        .string()
        .optional()
        .refine(
            (val) => validateCommaSeparatedValues(val, VALID_DAYS),
            "Must be comma-separated list of valid week days"
        )
        .transform((val) =>
            val
                ? (val
                      .split(",")
                      .map((item) => item.trim())
                      .filter(Boolean) as (typeof VALID_DAYS)[number][])
                : undefined
        ),
    WeeklyRecurrenceFrequency: z
        .string()
        .optional()
        .transform((val) => (val ? parseInt(val, 10) : undefined))
        .refine(
            (val) => val === undefined || (!isNaN(val) && val > 0 && val <= 6),
            "Must be a number between 1 and 6"
        ),
    MonthlyRecurrenceMonths: z
        .string()
        .optional()
        .refine(
            (val) => validateCommaSeparatedValues(val, VALID_MONTHS),
            "Must be comma-separated list of valid months"
        )
        .transform((val) =>
            val
                ? (val
                      .split(",")
                      .map((item) => item.trim())
                      .filter(Boolean) as (typeof VALID_MONTHS)[number][])
                : undefined
        ),
    EveryTwoMonthsRecurrenceTwoMonths: z
        .string()
        .optional()
        .refine(
            (val) => validateCommaSeparatedValues(val, VALID_TWO_MONTHS),
            "Must be comma-separated list of valid two-month periods"
        )
        .transform((val) =>
            val
                ? (val
                      .split(",")
                      .map((item) => item.trim())
                      .filter(Boolean) as (typeof VALID_TWO_MONTHS)[number][])
                : undefined
        ),
    QuarterlyRecurrenceQuarters: z
        .string()
        .optional()
        .refine(
            (val) => validateCommaSeparatedValues(val, VALID_QUARTERS),
            "Must be comma-separated list of valid quarters"
        )
        .transform((val) =>
            val
                ? (val
                      .split(",")
                      .map((item) => item.trim())
                      .filter(Boolean) as (typeof VALID_QUARTERS)[number][])
                : undefined
        ),
    HalfYearlyRecurrenceHalfYears: z
        .string()
        .optional()
        .refine(
            (val) => validateCommaSeparatedValues(val, VALID_HALF_YEARS),
            "Must be comma-separated list of valid half-years"
        )
        .transform((val) =>
            val
                ? (val
                      .split(",")
                      .map((item) => item.trim())
                      .filter(Boolean) as (typeof VALID_HALF_YEARS)[number][])
                : undefined
        ),
    DateRecurrenceDay: z
        .string()
        .optional()
        .refine(
            (val) => !val || moment(val, "YYYY-MM-DD", true).isValid(),
            "Must be in YYYY-MM-DD format"
        ),
    RecurrenceStartDate: z
        .string()
        .optional()
        .refine(
            (val) => !val || moment(val, "YYYY-MM-DD", true).isValid(),
            "Must be in YYYY-MM-DD format"
        ),
    NotificationSentDaysBefore: z
        .string()
        .optional()
        .transform((val) => (val ? parseInt(val, 10) : 5))
        .refine(
            (val) => !isNaN(val) && val >= 0,
            "Must be a non-negative number"
        ),
    RecurrenceOffsetDays: z
        .string()
        .optional()
        .transform((val) => (val ? parseInt(val, 10) : undefined))
        .refine(
            (val) => !val || (!isNaN(val) && val >= 1 && val <= 31),
            "Must be a number between 1 and 31"
        ),
});
